import { useState, useEffect } from 'react';
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc, doc
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { RESTAURANT_ID } from '../constants';
import { logAudit } from '../auditLog';
import { Empty, Icon, Loading, Modal, PageHead, Switch, tl, useToast } from '../ui';
import './menu.css';
import OptionTemplates, { loadOptionTemplates } from '../components/OptionTemplates';

// Sürüklenebilir satır (sıralama modu).
function SortableItem({ id, className, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    cursor: 'grab',
    position: 'relative',
    zIndex: isDragging ? 2 : undefined,
  };
  return <div ref={setNodeRef} style={style} className={className} {...attributes} {...listeners}>{children}</div>;
}

// Listede fiyatı formu açmadan değiştirmek için küçük giriş kutusu.
function InlinePrice({ value, onSave }) {
  const [text, setText] = useState(String(value ?? ''));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setText(String(value ?? '')); }, [value]);
  const commit = async () => {
    const n = parseFloat(String(text).replace(',', '.'));
    if (Number.isNaN(n) || n <= 0) { setText(String(value ?? '')); return; }
    if (n === Number(value)) return;
    setSaving(true);
    const ok = await onSave(Math.round(n * 100) / 100);
    if (!ok) setText(String(value ?? ''));
    setSaving(false);
  };
  return (
    <label className={`km-inline-price${saving ? ' is-saving' : ''}`} onPointerDown={(e) => e.stopPropagation()}>
      <input
        className="tnum"
        inputMode="decimal"
        value={text}
        disabled={saving}
        aria-label="Fiyat (TL)"
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') { setText(String(value ?? '')); e.currentTarget.blur(); }
        }}
      />
      <span>TL</span>
    </label>
  );
}

function Menu() {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [confirmState, setConfirmState] = useState(null);
  const [activeTab, setActiveTab] = useState('products');
  const [optionTemplates, setOptionTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [combos, setCombos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showBulkMenu, setShowBulkMenu] = useState(false);
  const [showBulkDiscountForm, setShowBulkDiscountForm] = useState(false);
  const [bulkDiscountForm, setBulkDiscountForm] = useState({ discountType: 'percent', discountValue: '', targetCategory: 'all' });
  const [bulkLoading, setBulkLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [categoryUploading, setCategoryUploading] = useState(false);
  const [sortingEnabled, setSortingEnabled] = useState(false);
  const [pageSize, setPageSize] = useState(() => {
    try { return Number(localStorage.getItem('kadihan_menu_page_size')) === 50 ? 50 : 25; } catch { return 25; }
  });
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | off
  const [missingFilter, setMissingFilter] = useState(''); // '' | image | description
  useEffect(() => { setPage(1); }, [selectedCategory, search, pageSize, statusFilter, missingFilter]);
  const changePageSize = (n) => {
    setPageSize(n);
    try { localStorage.setItem('kadihan_menu_page_size', String(n)); } catch { /* yok say */ }
  };

  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '', description: '', price: '', category: '',
    imageUrl: '', isAvailable: true, isPopular: false, isCampaign: false, isUpsell: false,
    campaignPrice: '', options: [], weightGrams: '', calories: ''
  });

  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', order: '', imageUrl: '', isActive: true });

  const [showOptionForm, setShowOptionForm] = useState(false);
  const [optionForm, setOptionForm] = useState({ title: '', required: false, items: [] });
  const [optionItem, setOptionItem] = useState({ name: '', extraPrice: '' });

  const [showCategoryDiscountForm, setShowCategoryDiscountForm] = useState(false);
  const [categoryDiscountForm, setCategoryDiscountForm] = useState({ targetCategory: '', discountType: 'percent', discountValue: '' });
  const [categoryDiscountLoading, setCategoryDiscountLoading] = useState(false);

  const [showComboForm, setShowComboForm] = useState(false);
  const [editingCombo, setEditingCombo] = useState(null);
  const [comboForm, setComboForm] = useState({
    name: '', description: '', price: '', imageUrl: '', isAvailable: true, order: '', items: []
  });
  const [comboItem, setComboItem] = useState({ productId: '', quantity: '1' });
  const [comboUploading, setComboUploading] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setFetchError('');
    try {
      const catSnap = await getDocs(collection(db, 'restaurants', RESTAURANT_ID, 'categories'));
      const cats = catSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      cats.sort((a, b) => (a.order || 0) - (b.order || 0));
      setCategories(cats);

      const prodSnap = await getDocs(collection(db, 'restaurants', RESTAURANT_ID, 'products'));
      const prods = prodSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      prods.sort((a, b) => (a.order || 0) - (b.order || 0));
      setProducts(prods);

      const comboSnap = await getDocs(collection(db, 'restaurants', RESTAURANT_ID, 'combos'));
      const combosData = comboSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      combosData.sort((a, b) => (a.order || 0) - (b.order || 0));
      setCombos(combosData);
    } catch (err) {
      console.error(err);
      setFetchError('Menü verileri yüklenemedi. Lütfen sayfayı yenileyin.');
    }
    setLoading(false);
  };

  const filteredProducts = selectedCategory === 'all'
    ? products : products.filter(p => p.category === selectedCategory);

  const handleProductDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = filteredProducts.findIndex(p => p.id === active.id);
    const newIndex = filteredProducts.findIndex(p => p.id === over.id);
    const newOrder = arrayMove(filteredProducts, oldIndex, newIndex);
    if (selectedCategory === 'all') {
      setProducts(newOrder);
    } else {
      const otherProducts = products.filter(p => p.category !== selectedCategory);
      setProducts([...otherProducts, ...newOrder]);
    }
    await Promise.all(newOrder.map((p, index) =>
      updateDoc(doc(db, 'restaurants', RESTAURANT_ID, 'products', p.id), { order: index })
    ));
  };

  const handleCategoryDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = categories.findIndex(c => c.id === active.id);
    const newIndex = categories.findIndex(c => c.id === over.id);
    const newOrder = arrayMove(categories, oldIndex, newIndex);
    setCategories(newOrder);
    await Promise.all(newOrder.map((c, index) =>
      updateDoc(doc(db, 'restaurants', RESTAURANT_ID, 'categories', c.id), { order: index })
    ));
  };

  const handleComboDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = combos.findIndex(c => c.id === active.id);
    const newIndex = combos.findIndex(c => c.id === over.id);
    const newOrder = arrayMove(combos, oldIndex, newIndex);
    setCombos(newOrder);
    await Promise.all(newOrder.map((c, index) =>
      updateDoc(doc(db, 'restaurants', RESTAURANT_ID, 'combos', c.id), { order: index })
    ));
  };

  const uploadImage = (file, folder = 'products') => {
    return new Promise((resolve, reject) => {
      const storageRef = ref(storage, `${folder}/${Date.now()}_${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);
      setUploading(true);
      uploadTask.on('state_changed',
        (snapshot) => setUploadProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)),
        (error) => { setUploading(false); reject(error); },
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          setUploading(false);
          setUploadProgress(0);
          resolve(url);
        }
      );
    });
  };

  const handleProductImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const url = await uploadImage(file, 'products');
      setProductForm(p => ({ ...p, imageUrl: url }));
    } catch (err) { toast('Görsel yüklenemedi: ' + err.message, 'error'); }
  };

  const handleCategoryImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCategoryUploading(true);
    try {
      const url = await uploadImage(file, 'categories');
      setCategoryForm(p => ({ ...p, imageUrl: url }));
    } catch (err) { toast('Görsel yüklenemedi: ' + err.message, 'error'); }
    setCategoryUploading(false);
  };

  const handleComboImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setComboUploading(true);
    try {
      const storageRef = ref(storage, `combos/${Date.now()}_${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);
      uploadTask.on('state_changed', null,
        (err) => { toast('Hata: ' + err.message, 'error'); setComboUploading(false); },
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          setComboForm(p => ({ ...p, imageUrl: url }));
          setComboUploading(false);
        }
      );
    } catch (err) { toast('Görsel yüklenemedi: ' + err.message, 'error'); setComboUploading(false); }
  };

  const bulkSetAvailable = async (isAvailable) => {
    setBulkLoading(true);
    const targetProducts = selectedCategory === 'all' ? products : products.filter(p => p.category === selectedCategory);
    try {
      await Promise.all(targetProducts.map(p =>
        updateDoc(doc(db, 'restaurants', RESTAURANT_ID, 'products', p.id), { isAvailable })
      ));
      await fetchData();
    } catch (e) {
      console.error('Toplu güncelleme başarısız:', e);
      toast('Güncelleme sırasında hata oluştu, bazı ürünler değişmemiş olabilir. Sayfayı yenileyip kontrol edin.', 'error');
    }
    setBulkLoading(false);
    setShowBulkMenu(false);
  };

  const bulkSetDiscount = async () => {
    if (!bulkDiscountForm.discountValue) { toast('İndirim değeri zorunlu', 'error'); return; }
    const discountVal = parseFloat(bulkDiscountForm.discountValue);
    if (isNaN(discountVal) || discountVal <= 0) { toast('İndirim değeri sıfırdan büyük olmalıdır.', 'error'); return; }
    if (bulkDiscountForm.discountType === 'percent' && discountVal >= 100) { toast('Yüzde indirim 100\'den küçük olmalıdır.', 'error'); return; }
    setBulkLoading(true);
    const targetProducts = bulkDiscountForm.targetCategory === 'all'
      ? products : products.filter(p => p.category === bulkDiscountForm.targetCategory);
    try {
      await Promise.all(targetProducts.map(p => {
        let campaignPrice = bulkDiscountForm.discountType === 'percent'
          ? p.price - (p.price * parseFloat(bulkDiscountForm.discountValue) / 100)
          : p.price - parseFloat(bulkDiscountForm.discountValue);
        campaignPrice = Math.max(0, Math.round(campaignPrice));
        return updateDoc(doc(db, 'restaurants', RESTAURANT_ID, 'products', p.id), { isCampaign: true, campaignPrice });
      }));
      await fetchData();
    } catch (e) {
      console.error('Toplu indirim uygulanamadı:', e);
      toast('İndirim sırasında hata oluştu, bazı ürünler değişmemiş olabilir. Sayfayı yenileyip kontrol edin.', 'error');
    }
    setBulkLoading(false);
    setShowBulkDiscountForm(false);
    setShowBulkMenu(false);
  };

  const bulkRemoveDiscount = async () => {
    setBulkLoading(true);
    const targetProducts = selectedCategory === 'all' ? products : products.filter(p => p.category === selectedCategory);
    try {
      await Promise.all(targetProducts.map(p =>
        updateDoc(doc(db, 'restaurants', RESTAURANT_ID, 'products', p.id), { isCampaign: false, campaignPrice: null })
      ));
      await fetchData();
    } catch (e) {
      console.error('Kampanya kaldırma hatası:', e);
      toast('Hata oluştu, bazı ürünler değişmemiş olabilir. Sayfayı yenileyip kontrol edin.', 'error');
    }
    setBulkLoading(false);
    setShowBulkMenu(false);
  };

  const applyCategoryDiscount = async () => {
    if (!categoryDiscountForm.targetCategory) { toast('Kategori seçin', 'error'); return; }
    if (!categoryDiscountForm.discountValue) { toast('İndirim değeri zorunlu', 'error'); return; }
    const catDiscountVal = parseFloat(categoryDiscountForm.discountValue);
    if (isNaN(catDiscountVal) || catDiscountVal <= 0) { toast('İndirim değeri sıfırdan büyük olmalıdır.', 'error'); return; }
    if (categoryDiscountForm.discountType === 'percent' && catDiscountVal >= 100) { toast('Yüzde indirim 100\'den küçük olmalıdır.', 'error'); return; }
    setCategoryDiscountLoading(true);
    const targetProducts = products.filter(p => p.category === categoryDiscountForm.targetCategory);
    await Promise.all(targetProducts.map(p => {
      let campaignPrice = categoryDiscountForm.discountType === 'percent'
        ? p.price - (p.price * parseFloat(categoryDiscountForm.discountValue) / 100)
        : p.price - parseFloat(categoryDiscountForm.discountValue);
      campaignPrice = Math.max(0, Math.round(campaignPrice));
      return updateDoc(doc(db, 'restaurants', RESTAURANT_ID, 'products', p.id), { isCampaign: true, campaignPrice });
    }));
    await fetchData();
    setCategoryDiscountLoading(false);
    setShowCategoryDiscountForm(false);
    setCategoryDiscountForm({ targetCategory: '', discountType: 'percent', discountValue: '' });
  };

  const removeCategoryDiscount = async (categoryName) => {
    setBulkLoading(true);
    const targetProducts = products.filter(p => p.category === categoryName);
    await Promise.all(targetProducts.map(p =>
      updateDoc(doc(db, 'restaurants', RESTAURANT_ID, 'products', p.id), { isCampaign: false, campaignPrice: null })
    ));
    await fetchData();
    setBulkLoading(false);
  };

  const toggleCategoryActive = async (cat) => {
    const newVal = cat.isActive === false ? true : false;
    await updateDoc(doc(db, 'restaurants', RESTAURANT_ID, 'categories', cat.id), { isActive: newVal });
    const catProducts = products.filter(p => p.category === cat.name);
    await Promise.all(catProducts.map(p =>
      updateDoc(doc(db, 'restaurants', RESTAURANT_ID, 'products', p.id), { isAvailable: newVal })
    ));
    await fetchData();
  };

  const refreshTemplates = () => { loadOptionTemplates().then(setOptionTemplates).catch(() => {}); };
  const addTemplateToProduct = (id) => {
    const t = optionTemplates.find((x) => x.id === id);
    if (!t) return;
    setProductForm((prev) => ({ ...prev, options: [...prev.options, { title: t.title, required: !!t.required, items: t.items || [], templateId: t.id }] }));
  };

  const openProductForm = (product = null) => {
    refreshTemplates();
    if (product) {
      setEditingProduct(product);
      setProductForm({
        name: product.name || '', description: product.description || '',
        price: product.price || '', category: product.category || '',
        imageUrl: product.imageUrl || '', isAvailable: product.isAvailable ?? true,
        isPopular: product.isPopular ?? false, isCampaign: product.isCampaign ?? false, isUpsell: product.isUpsell ?? false,
        campaignPrice: product.campaignPrice || '', options: product.options || [],
        weightGrams: product.weightGrams || '', calories: product.calories || '',
      });
    } else {
      setEditingProduct(null);
      setProductForm({
        name: '', description: '', price: '',
        category: selectedCategory !== 'all' ? selectedCategory : '',
        imageUrl: '', isAvailable: true, isPopular: false, isCampaign: false, isUpsell: false,
        campaignPrice: '', options: []
      });
    }
    setShowProductForm(true);
  };

  const openProductFormForCategory = (catName) => {
    refreshTemplates();
    setEditingProduct(null);
    setProductForm({
      name: '', description: '', price: '',
      category: catName,
      imageUrl: '', isAvailable: true, isPopular: false, isCampaign: false, isUpsell: false,
      campaignPrice: '', options: []
    });
    setShowProductForm(true);
  };

  const saveProduct = async () => {
    if (!productForm.name || !productForm.price || !productForm.category) {
      toast('Lütfen zorunlu alanları doldurun', 'error'); return;
    }
    const parsedPrice = parseFloat(productForm.price);
    if (isNaN(parsedPrice) || parsedPrice <= 0) {
      toast('Fiyat sıfırdan büyük olmalıdır.', 'error'); return;
    }
    if (productForm.isCampaign && productForm.campaignPrice) {
      const parsedCampaign = parseFloat(productForm.campaignPrice);
      if (isNaN(parsedCampaign) || parsedCampaign <= 0) {
        toast('Kampanya fiyatı sıfırdan büyük olmalıdır.', 'error'); return;
      }
      if (parsedCampaign >= parsedPrice) {
        toast('Kampanya fiyatı normal fiyattan düşük olmalıdır.', 'error'); return;
      }
    }
    const data = {
      name: productForm.name, description: productForm.description,
      price: parsedPrice, category: productForm.category,
      imageUrl: productForm.imageUrl, isAvailable: productForm.isAvailable,
      isPopular: productForm.isPopular, isCampaign: productForm.isCampaign, isUpsell: productForm.isUpsell,
      campaignPrice: productForm.isCampaign && productForm.campaignPrice ? parseFloat(productForm.campaignPrice) : null,
      options: productForm.options,
      weightGrams: productForm.weightGrams ? parseInt(productForm.weightGrams) : null,
      calories: productForm.calories ? parseInt(productForm.calories) : null,
    };
    if (editingProduct) {
      await updateDoc(doc(db, 'restaurants', RESTAURANT_ID, 'products', editingProduct.id), data);
    } else {
      data.order = products.length;
      await addDoc(collection(db, 'restaurants', RESTAURANT_ID, 'products'), data);
    }
    setShowProductForm(false);
    fetchData();
  };

  const deleteProduct = async (id) => {
    await deleteDoc(doc(db, 'restaurants', RESTAURANT_ID, 'products', id));
    logAudit('product_deleted', id);
    fetchData();
  };

  const savePrice = async (product, price) => {
    if (product.isCampaign && Number(product.campaignPrice) >= price) {
      toast('Yeni fiyat kampanya fiyatından yüksek olmalı. Önce kampanya fiyatını düzenleyin.', 'error');
      return false;
    }
    const old = product.price;
    setProducts((list) => list.map((p) => (p.id === product.id ? { ...p, price } : p)));
    try {
      await updateDoc(doc(db, 'restaurants', RESTAURANT_ID, 'products', product.id), { price });
      logAudit('product_price_changed', product.id, { from: old, to: price });
      toast(`${product.name}: ${tl(price)}`, 'ok');
      return true;
    } catch {
      setProducts((list) => list.map((p) => (p.id === product.id ? { ...p, price: old } : p)));
      toast('Fiyat kaydedilemedi, tekrar deneyin.', 'error');
      return false;
    }
  };

  const toggleAvailable = async (product) => {
    const next = !product.isAvailable;
    setProducts((list) => list.map((p) => (p.id === product.id ? { ...p, isAvailable: next } : p)));
    try {
      await updateDoc(doc(db, 'restaurants', RESTAURANT_ID, 'products', product.id), { isAvailable: next });
      toast(next ? `${product.name} satışa açıldı` : `${product.name} stokta yok olarak işaretlendi`, 'ok');
    } catch {
      setProducts((list) => list.map((p) => (p.id === product.id ? { ...p, isAvailable: !next } : p)));
      toast('Güncellenemedi, tekrar deneyin.', 'error');
    }
  };

  const openCategoryForm = (cat = null) => {
    if (cat) {
      setEditingCategory(cat);
      setCategoryForm({ name: cat.name || '', order: cat.order || '', imageUrl: cat.imageUrl || '', isActive: cat.isActive !== false });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: '', order: categories.length, imageUrl: '', isActive: true });
    }
    setShowCategoryForm(true);
  };

  const saveCategory = async () => {
    if (!categoryForm.name) { toast('Kategori adı zorunlu', 'error'); return; }
    const data = { name: categoryForm.name, order: parseInt(categoryForm.order) || 0, imageUrl: categoryForm.imageUrl || '', isActive: categoryForm.isActive };
    if (editingCategory) {
      await updateDoc(doc(db, 'restaurants', RESTAURANT_ID, 'categories', editingCategory.id), data);
    } else {
      await addDoc(collection(db, 'restaurants', RESTAURANT_ID, 'categories'), data);
    }
    setShowCategoryForm(false);
    fetchData();
  };

  const deleteCategory = async (id) => {
    await deleteDoc(doc(db, 'restaurants', RESTAURANT_ID, 'categories', id));
    fetchData();
  };

  const openComboForm = (combo = null) => {
    if (combo) {
      setEditingCombo(combo);
      setComboForm({
        name: combo.name || '', description: combo.description || '',
        price: combo.price || '', imageUrl: combo.imageUrl || '',
        isAvailable: combo.isAvailable ?? true, order: combo.order || '',
        items: combo.items || [],
      });
    } else {
      setEditingCombo(null);
      setComboForm({ name: '', description: '', price: '', imageUrl: '', isAvailable: true, order: combos.length, items: [] });
    }
    setComboItem({ productId: '', quantity: '1' });
    setShowComboForm(true);
  };

  const addComboItem = () => {
    if (!comboItem.productId) { toast('Ürün seçin', 'error'); return; }
    const product = products.find(p => p.id === comboItem.productId);
    if (!product) return;
    const exists = comboForm.items.find(i => i.productId === comboItem.productId);
    if (exists) { toast('Bu ürün zaten eklendi', 'error'); return; }
    setComboForm(prev => ({
      ...prev,
      items: [...prev.items, { productId: comboItem.productId, productName: product.name, quantity: parseInt(comboItem.quantity) || 1 }]
    }));
    setComboItem({ productId: '', quantity: '1' });
  };

  const removeComboItem = (index) => {
    setComboForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const saveCombo = async () => {
    if (!comboForm.name || !comboForm.price) { toast('İsim ve fiyat zorunlu', 'error'); return; }
    if (comboForm.items.length === 0) { toast('En az bir ürün ekleyin', 'error'); return; }
    const parsedComboPrice = parseFloat(comboForm.price);
    if (isNaN(parsedComboPrice) || parsedComboPrice <= 0) { toast('Kombo fiyatı sıfırdan büyük olmalıdır.', 'error'); return; }
    const data = {
      name: comboForm.name, description: comboForm.description,
      price: parsedComboPrice, imageUrl: comboForm.imageUrl || '',
      isAvailable: comboForm.isAvailable, order: parseInt(comboForm.order) || 0,
      items: comboForm.items,
    };
    if (editingCombo) {
      await updateDoc(doc(db, 'restaurants', RESTAURANT_ID, 'combos', editingCombo.id), data);
    } else {
      await addDoc(collection(db, 'restaurants', RESTAURANT_ID, 'combos'), data);
    }
    setShowComboForm(false);
    fetchData();
  };

  const deleteCombo = async (id) => {
    await deleteDoc(doc(db, 'restaurants', RESTAURANT_ID, 'combos', id));
    fetchData();
  };

  const toggleComboAvailable = async (combo) => {
    await updateDoc(doc(db, 'restaurants', RESTAURANT_ID, 'combos', combo.id), { isAvailable: !combo.isAvailable });
    fetchData();
  };

  const addOptionItem = () => {
    if (!optionItem.name) return;
    setOptionForm(prev => ({
      ...prev,
      items: [...prev.items, { name: optionItem.name, extraPrice: parseFloat(optionItem.extraPrice) || 0 }]
    }));
    setOptionItem({ name: '', extraPrice: '' });
  };

  const removeOptionItem = (index) => {
    setOptionForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const addOptionToProduct = () => {
    if (!optionForm.title || optionForm.items.length === 0) { toast('Opsiyon başlığı ve en az bir seçenek zorunlu', 'error'); return; }
    setProductForm(prev => ({ ...prev, options: [...prev.options, { ...optionForm }] }));
    setOptionForm({ title: '', required: false, items: [] });
    setShowOptionForm(false);
  };

  const removeOption = (index) => {
    setProductForm(prev => ({ ...prev, options: prev.options.filter((_, i) => i !== index) }));
  };

  if (loading) return <div className="k-page"><Loading text="Menü yükleniyor…" /></div>;
  if (fetchError) return (
    <div className="k-page">
      <div className="k-alert error">
        {fetchError}
        <button type="button" className="k-btn k-btn-secondary k-btn-sm" style={{ marginLeft: 12 }} onClick={() => { setLoading(true); fetchData(); }}>Tekrar dene</button>
      </div>
    </div>
  );

  const ask = (title, text, confirmLabel, onConfirm) => setConfirmState({ title, text, confirmLabel, onConfirm });
  const q = search.trim().toLocaleLowerCase('tr-TR');
  const isOn = (p) => p.isAvailable !== false;
  const scopeProducts = sortingEnabled ? filteredProducts : filteredProducts.filter((p) =>
    (!q || [p.name, p.description, p.category].some((f) => String(f || '').toLocaleLowerCase('tr-TR').includes(q)))
    && (missingFilter !== 'image' || !p.imageUrl)
    && (missingFilter !== 'description' || !String(p.description || '').trim()));
  const activeCount = scopeProducts.filter(isOn).length;
  const visibleProducts = sortingEnabled || statusFilter === 'all' ? scopeProducts
    : scopeProducts.filter((p) => (statusFilter === 'active' ? isOn(p) : !isOn(p)));
  const missingImageCount = filteredProducts.filter((p) => !p.imageUrl).length;
  const missingDescCount = filteredProducts.filter((p) => !String(p.description || '').trim()).length;
  const pageCount = Math.max(1, Math.ceil(visibleProducts.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * pageSize;
  const pagedProducts = visibleProducts.slice(pageStart, pageStart + pageSize);
  const pageNumbers = (() => {
    if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
    const set = new Set([1, pageCount, currentPage - 1, currentPage, currentPage + 1]);
    const nums = [...set].filter((n) => n >= 1 && n <= pageCount).sort((a, b) => a - b);
    const out = [];
    nums.forEach((n, i) => { if (i > 0 && n - nums[i - 1] > 1) out.push('…' + n); out.push(n); });
    return out;
  })();
  const goPage = (n) => {
    setPage(n);
    document.querySelector('.km-list-card')?.scrollIntoView({ block: 'start' });
  };
  const outOfStock = products.filter((p) => p.isAvailable === false).length;
  const scopeLabel = selectedCategory === 'all' ? 'Tüm ürünler' : selectedCategory;
  const priceCell = (p) => (
    <div className="km-price">
      {sortingEnabled
        ? <strong className="tnum">{tl(p.price)}</strong>
        : <InlinePrice value={p.price} onSave={(v) => savePrice(p, v)} />}
      {p.isCampaign && p.campaignPrice ? <div className="km-price-camp tnum">Kampanya: {tl(p.campaignPrice)}</div> : null}
    </div>
  );
  const thumb = (url, icon = 'dish') => (
    url ? <img className="km-thumb" src={url} alt="" loading="lazy" /> : <span className="km-thumb is-empty"><Icon name={icon} size={18} /></span>
  );

  const productRow = (p) => (
    <>
      {thumb(p.imageUrl)}
      <div className="km-info">
        <div className="km-name">{p.name}</div>
        {p.description && <div className="km-desc">{p.description}</div>}
        <div className="km-tags">
          {selectedCategory === 'all' && <span className="k-badge">{p.category}</span>}
          {p.isPopular && <span className="k-badge gold">Popüler</span>}
          {p.isCampaign && <span className="k-badge amber">Kampanya</span>}
          {p.isUpsell && <span className="k-badge blue">Yanında iyi gider</span>}
          {p.options?.length > 0 && <span className="k-badge">{p.options.length} seçenek grubu</span>}
        </div>
      </div>
      {priceCell(p)}
      <label className="km-stock" onPointerDown={(e) => e.stopPropagation()}>
        <Switch checked={p.isAvailable !== false} onChange={() => toggleAvailable(p)} />
        <span className={p.isAvailable !== false ? '' : 'off'}>{p.isAvailable !== false ? 'Satışta' : 'Stokta yok'}</span>
      </label>
      <div className="km-actions" onPointerDown={(e) => e.stopPropagation()}>
        <button type="button" className="k-btn k-btn-secondary k-btn-sm" onClick={() => openProductForm(p)}>Düzenle</button>
      </div>
    </>
  );

  return (
    <div className="k-page">
      <PageHead desc={`${products.length} ürün · ${categories.length} kategori · ${combos.length} kombo${outOfStock ? ` · ${outOfStock} ürün stokta yok` : ''}`}>
        <div className="k-seg">
          {[['products', 'Ürünler'], ['categories', 'Kategoriler'], ['combos', 'Kombolar'], ['templates', 'Seçenek şablonları']].map(([id, label]) => (
            <button key={id} type="button" className={activeTab === id ? 'on' : ''} onClick={() => setActiveTab(id)}>{label}</button>
          ))}
        </div>
        {activeTab !== 'templates' && (
          <button type="button" className={`k-btn ${sortingEnabled ? 'k-btn-gold' : 'k-btn-secondary'}`} onClick={() => setSortingEnabled(!sortingEnabled)}>
            <Icon name="menu" size={15} />{sortingEnabled ? 'Sıralamayı bitir' : 'Sırala'}
          </button>
        )}
        {activeTab === 'categories' && (
          <button type="button" className="k-btn k-btn-secondary" onClick={() => setShowCategoryDiscountForm(true)}><Icon name="tag" size={15} />Kategori indirimi</button>
        )}
        {activeTab === 'products' && (
          <div className="km-menu-wrap">
            <button type="button" className="k-btn k-btn-secondary" onClick={() => setShowBulkMenu(!showBulkMenu)} disabled={bulkLoading}>
              {bulkLoading ? 'İşleniyor…' : 'Toplu işlem'}
            </button>
            {showBulkMenu && (
              <div className="km-dropdown" onMouseLeave={() => setShowBulkMenu(false)}>
                <div className="km-dropdown-label">{scopeLabel}</div>
                <button type="button" onClick={() => bulkSetAvailable(true)}>Tümünü satışa aç</button>
                <button type="button" onClick={() => bulkSetAvailable(false)}>Tümünü stokta yok yap</button>
                <button type="button" onClick={() => { setShowBulkDiscountForm(true); setShowBulkMenu(false); }}>Toplu indirim tanımla</button>
                <button type="button" className="danger" onClick={() => { setShowBulkMenu(false); ask('Kampanyaları kaldır', `${scopeLabel} için tanımlı tüm indirimler kaldırılacak.`, 'Kaldır', bulkRemoveDiscount); }}>Toplu indirimi kaldır</button>
              </div>
            )}
          </div>
        )}
        {activeTab !== 'templates' && <button type="button" className="k-btn k-btn-primary" onClick={() => {
          if (activeTab === 'products') openProductForm();
          else if (activeTab === 'categories') openCategoryForm();
          else openComboForm();
        }}>
          <Icon name="plus" size={16} />{activeTab === 'products' ? 'Ürün ekle' : activeTab === 'categories' ? 'Kategori ekle' : 'Kombo ekle'}
        </button>}
      </PageHead>

      {sortingEnabled && activeTab !== 'templates' && <div className="k-alert warn">Satırları tutup sürükleyerek sıralayın. Değişiklikler otomatik kaydedilir.</div>}

      {activeTab === 'products' && (
        <div className="km-layout">
          <nav className="km-cats k-card" aria-label="Kategoriler">
            {[{ key: 'all', name: 'Tüm ürünler', count: products.length }, ...categories.map((c) => ({ key: c.name, name: c.name, count: products.filter((p) => p.category === c.name).length, off: c.isActive === false }))].map((c) => (
              <button key={c.key} type="button" className={`km-cat${selectedCategory === c.key ? ' on' : ''}${c.off ? ' off' : ''}`} onClick={() => setSelectedCategory(c.key)}>
                <span>{c.name}</span><em className="tnum">{c.count}</em>
              </button>
            ))}
          </nav>
          <div className="k-card km-list-card">
            <div className="km-list-head">
              <div className="k-search" style={{ flex: 1 }}>
                <Icon name="search" size={16} />
                <input className="k-input" placeholder={`${scopeLabel} içinde ara`} value={search} onChange={(e) => setSearch(e.target.value)} disabled={sortingEnabled} />
              </div>
              {selectedCategory !== 'all' && (
                <button type="button" className="k-btn k-btn-secondary k-btn-sm" onClick={() => openProductFormForCategory(selectedCategory)}><Icon name="plus" size={14} />Bu kategoriye ekle</button>
              )}
            </div>
            {!sortingEnabled && (
              <div className="km-filter-bar">
                <div className="k-seg">
                  <button type="button" className={statusFilter === 'all' ? 'on' : ''} onClick={() => setStatusFilter('all')}>Tümü<span className="k-seg-count">{scopeProducts.length}</span></button>
                  <button type="button" className={statusFilter === 'active' ? 'on' : ''} onClick={() => setStatusFilter('active')}>Satışta<span className="k-seg-count">{activeCount}</span></button>
                  <button type="button" className={statusFilter === 'off' ? 'on' : ''} onClick={() => setStatusFilter('off')}>Stokta yok<span className="k-seg-count">{scopeProducts.length - activeCount}</span></button>
                </div>
                <div className="km-chips">
                  <button type="button" className={`km-chip${missingFilter === 'image' ? ' on' : ''}`} onClick={() => setMissingFilter(missingFilter === 'image' ? '' : 'image')}>Görseli olmayanlar <b className="tnum">{missingImageCount}</b></button>
                  <button type="button" className={`km-chip${missingFilter === 'description' ? ' on' : ''}`} onClick={() => setMissingFilter(missingFilter === 'description' ? '' : 'description')}>Açıklaması olmayanlar <b className="tnum">{missingDescCount}</b></button>
                </div>
              </div>
            )}
            {visibleProducts.length === 0 ? (
              <Empty icon="dish" title={q || missingFilter || statusFilter !== 'all' ? 'Bu filtreye uyan ürün yok' : 'Bu kategoride ürün yok'} />
            ) : sortingEnabled ? (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleProductDragEnd}>
                <SortableContext items={visibleProducts.map((p) => p.id)} strategy={verticalListSortingStrategy}>
                  <div className="km-rows">
                    {visibleProducts.map((p) => (
                      <SortableItem key={p.id} id={p.id} className={`km-row has-grip${p.isAvailable === false ? ' is-off' : ''}`}>
                        <span className="km-grip"><Icon name="menu" size={16} /></span>{productRow(p)}
                      </SortableItem>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            ) : (
              <>
                <div className="km-rows">
                  {pagedProducts.map((p) => <div key={p.id} className={`km-row${p.isAvailable === false ? ' is-off' : ''}`}>{productRow(p)}</div>)}
                </div>
                <div className="km-pager">
                  <span className="tnum">{pageStart + 1}–{pageStart + pagedProducts.length} / {visibleProducts.length} ürün</span>
                  <div className="km-pager-pages">
                    <button type="button" className="k-btn k-btn-secondary k-btn-sm" disabled={currentPage === 1} onClick={() => goPage(currentPage - 1)}>Önceki</button>
                    {pageNumbers.map((n) => (typeof n === 'string'
                      ? <span key={n} className="km-pager-gap">…</span>
                      : <button key={n} type="button" className={`km-pager-num${n === currentPage ? ' on' : ''}`} onClick={() => goPage(n)}>{n}</button>))}
                    <button type="button" className="k-btn k-btn-secondary k-btn-sm" disabled={currentPage === pageCount} onClick={() => goPage(currentPage + 1)}>Sonraki</button>
                  </div>
                  <label className="km-pager-size">
                    Sayfa başına
                    <select className="k-select" value={pageSize} onChange={(e) => changePageSize(Number(e.target.value))}>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </label>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {activeTab === 'categories' && (
        <div className="k-card">
          {categories.length === 0 ? <Empty icon="menu" title="Henüz kategori yok" /> : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCategoryDragEnd}>
              <SortableContext items={categories.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                <div className="km-rows">
                  {categories.map((cat) => {
                    const catProducts = products.filter((p) => p.category === cat.name);
                    const campaignCount = catProducts.filter((p) => p.isCampaign).length;
                    const active = cat.isActive !== false;
                    const Row = sortingEnabled ? SortableItem : 'div';
                    return (
                      <Row key={cat.id} id={cat.id} className={`km-row km-cat-row${sortingEnabled ? ' has-grip' : ''}${active ? '' : ' is-off'}`}>
                        {sortingEnabled && <span className="km-grip"><Icon name="menu" size={16} /></span>}
                        {thumb(cat.imageUrl, 'menu')}
                        <div className="km-info">
                          <div className="km-name">{cat.name}</div>
                          <div className="km-tags">
                            <span className="k-badge">{catProducts.length} ürün</span>
                            {campaignCount > 0 && (
                              <span className="k-badge amber">{campaignCount} ürün indirimli
                                <button type="button" className="km-badge-x" title="İndirimi kaldır" onPointerDown={(e) => e.stopPropagation()} onClick={() => ask('İndirimi kaldır', `“${cat.name}” kategorisindeki tüm indirimler kaldırılacak.`, 'Kaldır', () => removeCategoryDiscount(cat.name))}>×</button>
                              </span>
                            )}
                          </div>
                        </div>
                        <label className="km-stock" onPointerDown={(e) => e.stopPropagation()}>
                          <Switch checked={active} onChange={() => toggleCategoryActive(cat)} />
                          <span className={active ? '' : 'off'}>{active ? 'Menüde' : 'Gizli'}</span>
                        </label>
                        <div className="km-actions" onPointerDown={(e) => e.stopPropagation()}>
                          <button type="button" className="k-btn k-btn-secondary k-btn-sm" onClick={() => openProductFormForCategory(cat.name)}><Icon name="plus" size={14} />Ürün</button>
                          <button type="button" className="k-btn k-btn-secondary k-btn-sm" onClick={() => openCategoryForm(cat)}>Düzenle</button>
                        </div>
                      </Row>
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}

      {activeTab === 'combos' && (
        <div className="k-card">
          {combos.length === 0 ? <Empty icon="bag" title="Henüz kombo yok">Birkaç ürünü tek fiyatla satmak için kombo oluşturabilirsiniz.</Empty> : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleComboDragEnd}>
              <SortableContext items={combos.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                <div className="km-rows">
                  {combos.map((combo) => {
                    const Row = sortingEnabled ? SortableItem : 'div';
                    return (
                      <Row key={combo.id} id={combo.id} className={`km-row${sortingEnabled ? ' has-grip' : ''}${combo.isAvailable ? '' : ' is-off'}`}>
                        {sortingEnabled && <span className="km-grip"><Icon name="menu" size={16} /></span>}
                        {thumb(combo.imageUrl, 'bag')}
                        <div className="km-info">
                          <div className="km-name">{combo.name}</div>
                          <div className="km-desc">{(combo.items || []).map((i) => `${i.quantity}× ${i.productName}`).join(' · ')}</div>
                        </div>
                        <div className="km-price tnum"><strong>{tl(combo.price)}</strong></div>
                        <label className="km-stock" onPointerDown={(e) => e.stopPropagation()}>
                          <Switch checked={!!combo.isAvailable} onChange={() => toggleComboAvailable(combo)} />
                          <span className={combo.isAvailable ? '' : 'off'}>{combo.isAvailable ? 'Satışta' : 'Stokta yok'}</span>
                        </label>
                        <div className="km-actions" onPointerDown={(e) => e.stopPropagation()}>
                          <button type="button" className="k-btn k-btn-secondary k-btn-sm" onClick={() => openComboForm(combo)}>Düzenle</button>
                        </div>
                      </Row>
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}

      {activeTab === 'templates' && (
        <OptionTemplates products={products} categories={categories} onProductsChanged={fetchData} />
      )}

      {/* Ürün formu */}
      {showProductForm && (
        <Modal
          title={editingProduct ? 'Ürünü düzenle' : 'Yeni ürün'}
          width={620}
          onClose={() => setShowProductForm(false)}
          footer={(
            <>
              {editingProduct && (
                <button
                  type="button"
                  className="k-btn k-btn-danger"
                  style={{ marginRight: 'auto' }}
                  onClick={() => {
                    const p = editingProduct;
                    setShowProductForm(false);
                    ask('Ürünü kaldır', `“${p.name}” menüden kalıcı olarak kaldırılacak.`, 'Kaldır', () => deleteProduct(p.id));
                  }}
                >Ürünü kaldır</button>
              )}
              <button type="button" className="k-btn k-btn-secondary" onClick={() => setShowProductForm(false)}>Vazgeç</button>
              <button type="button" className="k-btn k-btn-primary" onClick={saveProduct} disabled={uploading}>{uploading ? 'Görsel yükleniyor…' : 'Kaydet'}</button>
            </>
          )}
        >
          <div className="km-form-grid">
            <label className="k-field km-span"><span>Ürün adı *</span><input className="k-input" value={productForm.name} onChange={(e) => setProductForm((p) => ({ ...p, name: e.target.value }))} /></label>
            <label className="k-field km-span"><span>Açıklama</span><textarea className="k-textarea" rows={2} value={productForm.description} onChange={(e) => setProductForm((p) => ({ ...p, description: e.target.value }))} /></label>
            <label className="k-field"><span>Fiyat (TL) *</span><input className="k-input" type="number" min="0.01" step="0.01" value={productForm.price} onChange={(e) => setProductForm((p) => ({ ...p, price: e.target.value }))} /></label>
            <label className="k-field"><span>Kategori *</span>
              <select className="k-select" value={productForm.category} onChange={(e) => setProductForm((p) => ({ ...p, category: e.target.value }))}>
                <option value="">Seçin</option>
                {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </label>
            <div className="k-field km-span">
              <span>Ürün görseli</span>
              <div className="km-upload">
                {productForm.imageUrl ? <img src={productForm.imageUrl} alt="" /> : <span className="km-upload-ph"><Icon name="dish" size={22} /></span>}
                <div className="km-upload-side">
                  <label className="k-btn k-btn-secondary k-btn-sm">
                    {uploading ? `Yükleniyor… %${uploadProgress}` : 'Bilgisayardan seç'}
                    <input type="file" accept="image/*" onChange={handleProductImageUpload} hidden disabled={uploading} />
                  </label>
                  {uploading && <div className="km-progress"><span style={{ width: `${uploadProgress}%` }} /></div>}
                  <input className="k-input" placeholder="veya görsel adresi (https://…)" value={productForm.imageUrl} onChange={(e) => setProductForm((p) => ({ ...p, imageUrl: e.target.value }))} />
                </div>
              </div>
            </div>
            <div className="km-span km-switches">
              {[['isAvailable', 'Satışta'], ['isPopular', 'Popüler'], ['isCampaign', 'Kampanyalı'], ['isUpsell', 'Yanında iyi gider']].map(([key, label]) => (
                <Switch key={key} checked={!!productForm[key]} onChange={(v) => setProductForm((p) => ({ ...p, [key]: v }))} label={label} />
              ))}
            </div>
            {productForm.isCampaign && (
              <label className="k-field"><span>Kampanya fiyatı (TL)</span><input className="k-input" type="number" min="0.01" step="0.01" value={productForm.campaignPrice} onChange={(e) => setProductForm((p) => ({ ...p, campaignPrice: e.target.value }))} /></label>
            )}
            <label className="k-field"><span>Gramaj (g)</span><input className="k-input" type="number" min="0" placeholder="isteğe bağlı" value={productForm.weightGrams || ''} onChange={(e) => setProductForm((p) => ({ ...p, weightGrams: e.target.value }))} /></label>
            <label className="k-field"><span>Kalori (kcal)</span><input className="k-input" type="number" min="0" placeholder="isteğe bağlı" value={productForm.calories || ''} onChange={(e) => setProductForm((p) => ({ ...p, calories: e.target.value }))} /></label>
          </div>
          <div className="km-options">
            <div className="km-options-head">
              <div>
                <strong>Seçenek grupları</strong>
                <p>Örn. “Acı seviyesi”, “Porsiyon”. Müşteri sipariş verirken seçer.</p>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {optionTemplates.some((t) => !productForm.options.some((o) => o.templateId === t.id)) && (
                  <select className="k-select" style={{ width: 'auto', height: 30 }} value="" onChange={(e) => addTemplateToProduct(e.target.value)} aria-label="Şablondan ekle">
                    <option value="">Şablondan ekle…</option>
                    {optionTemplates.filter((t) => !productForm.options.some((o) => o.templateId === t.id)).map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                  </select>
                )}
                <button type="button" className="k-btn k-btn-secondary k-btn-sm" onClick={() => setShowOptionForm(true)}><Icon name="plus" size={14} />Grup ekle</button>
              </div>
            </div>
            {productForm.options.length === 0 && <p className="km-muted">Seçenek grubu yok.</p>}
            {productForm.options.map((opt, i) => (
              <div key={i} className="km-option">
                <div>
                  <strong>{opt.title}</strong>{opt.required && <span className="k-badge red" style={{ marginLeft: 8 }}>Zorunlu</span>}{opt.templateId && <span className="k-badge" style={{ marginLeft: 6 }}>Şablon</span>}
                  <div className="km-muted">{opt.items.map((it) => `${it.name}${it.extraPrice > 0 ? ` (+${it.extraPrice} TL)` : ''}`).join(' · ')}</div>
                </div>
                <button type="button" className="k-icon-btn km-danger" title="Kaldır" onClick={() => removeOption(i)}><Icon name="x" size={16} /></button>
              </div>
            ))}
          </div>
        </Modal>
      )}

      {/* Seçenek grubu */}
      {showOptionForm && (
        <Modal
          title="Seçenek grubu ekle"
          width={460}
          onClose={() => setShowOptionForm(false)}
          footer={(
            <>
              <button type="button" className="k-btn k-btn-secondary" onClick={() => setShowOptionForm(false)}>Vazgeç</button>
              <button type="button" className="k-btn k-btn-primary" onClick={addOptionToProduct}>Ekle</button>
            </>
          )}
        >
          <label className="k-field"><span>Grup başlığı *</span><input className="k-input" placeholder="Örn. Acı seviyesi" value={optionForm.title} onChange={(e) => setOptionForm((p) => ({ ...p, title: e.target.value }))} /></label>
          <div style={{ marginBottom: 14 }}><Switch checked={optionForm.required} onChange={(v) => setOptionForm((p) => ({ ...p, required: v }))} label="Müşteri seçmek zorunda" /></div>
          <div className="k-field"><span>Seçenekler</span>
            {optionForm.items.map((it, i) => (
              <div key={i} className="km-option">
                <div>{it.name}{it.extraPrice > 0 && <span className="km-muted"> · +{it.extraPrice} TL</span>}</div>
                <button type="button" className="k-icon-btn km-danger" onClick={() => removeOptionItem(i)}><Icon name="x" size={16} /></button>
              </div>
            ))}
            <div className="km-inline">
              <input className="k-input" placeholder="Seçenek adı" value={optionItem.name} onChange={(e) => setOptionItem((p) => ({ ...p, name: e.target.value }))} />
              <input className="k-input" style={{ width: 120 }} placeholder="+ TL" type="number" value={optionItem.extraPrice} onChange={(e) => setOptionItem((p) => ({ ...p, extraPrice: e.target.value }))} />
              <button type="button" className="k-btn k-btn-secondary" onClick={addOptionItem}><Icon name="plus" size={15} /></button>
            </div>
          </div>
        </Modal>
      )}

      {/* Kategori formu */}
      {showCategoryForm && (
        <Modal
          title={editingCategory ? 'Kategoriyi düzenle' : 'Yeni kategori'}
          width={460}
          onClose={() => setShowCategoryForm(false)}
          footer={(
            <>
              {editingCategory && (
                <button
                  type="button"
                  className="k-btn k-btn-danger"
                  style={{ marginRight: 'auto' }}
                  onClick={() => {
                    const c = editingCategory;
                    setShowCategoryForm(false);
                    ask('Kategoriyi kaldır', `“${c.name}” kaldırılacak. İçindeki ürünler silinmez ama menüde kategorisiz kalır.`, 'Kaldır', () => deleteCategory(c.id));
                  }}
                >Kategoriyi kaldır</button>
              )}
              <button type="button" className="k-btn k-btn-secondary" onClick={() => setShowCategoryForm(false)}>Vazgeç</button>
              <button type="button" className="k-btn k-btn-primary" onClick={saveCategory} disabled={categoryUploading}>{categoryUploading ? 'Yükleniyor…' : 'Kaydet'}</button>
            </>
          )}
        >
          <label className="k-field"><span>Kategori adı *</span><input className="k-input" value={categoryForm.name} onChange={(e) => setCategoryForm((p) => ({ ...p, name: e.target.value }))} /></label>
          <label className="k-field"><span>Sıra</span><input className="k-input" type="number" value={categoryForm.order} onChange={(e) => setCategoryForm((p) => ({ ...p, order: e.target.value }))} /></label>
          <div style={{ marginBottom: 14 }}><Switch checked={categoryForm.isActive} onChange={(v) => setCategoryForm((p) => ({ ...p, isActive: v }))} label="Menüde göster" /></div>
          <div className="k-field">
            <span>Kategori görseli</span>
            <div className="km-upload">
              {categoryForm.imageUrl ? <img src={categoryForm.imageUrl} alt="" /> : <span className="km-upload-ph"><Icon name="menu" size={22} /></span>}
              <div className="km-upload-side">
                <label className="k-btn k-btn-secondary k-btn-sm">
                  {categoryUploading ? 'Yükleniyor…' : 'Bilgisayardan seç'}
                  <input type="file" accept="image/*" onChange={handleCategoryImageUpload} hidden disabled={categoryUploading} />
                </label>
                <input className="k-input" placeholder="veya görsel adresi (https://…)" value={categoryForm.imageUrl} onChange={(e) => setCategoryForm((p) => ({ ...p, imageUrl: e.target.value }))} />
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Kombo formu */}
      {showComboForm && (
        <Modal
          title={editingCombo ? 'Komboyu düzenle' : 'Yeni kombo'}
          width={580}
          onClose={() => setShowComboForm(false)}
          footer={(
            <>
              {editingCombo && (
                <button
                  type="button"
                  className="k-btn k-btn-danger"
                  style={{ marginRight: 'auto' }}
                  onClick={() => {
                    const c = editingCombo;
                    setShowComboForm(false);
                    ask('Komboyu kaldır', `“${c.name}” menüden kalıcı olarak kaldırılacak.`, 'Kaldır', () => deleteCombo(c.id));
                  }}
                >Komboyu kaldır</button>
              )}
              <button type="button" className="k-btn k-btn-secondary" onClick={() => setShowComboForm(false)}>Vazgeç</button>
              <button type="button" className="k-btn k-btn-primary" onClick={saveCombo} disabled={comboUploading}>Kaydet</button>
            </>
          )}
        >
          <div className="km-form-grid">
            <label className="k-field km-span"><span>Kombo adı *</span><input className="k-input" value={comboForm.name} onChange={(e) => setComboForm((p) => ({ ...p, name: e.target.value }))} /></label>
            <label className="k-field km-span"><span>Açıklama</span><input className="k-input" value={comboForm.description} onChange={(e) => setComboForm((p) => ({ ...p, description: e.target.value }))} /></label>
            <label className="k-field"><span>Fiyat (TL) *</span><input className="k-input" type="number" min="0.01" step="0.01" value={comboForm.price} onChange={(e) => setComboForm((p) => ({ ...p, price: e.target.value }))} /></label>
            <label className="k-field"><span>Sıra</span><input className="k-input" type="number" value={comboForm.order} onChange={(e) => setComboForm((p) => ({ ...p, order: e.target.value }))} /></label>
            <div className="k-field km-span">
              <span>Kombo görseli</span>
              <div className="km-upload">
                {comboForm.imageUrl ? <img src={comboForm.imageUrl} alt="" /> : <span className="km-upload-ph"><Icon name="bag" size={22} /></span>}
                <div className="km-upload-side">
                  <label className="k-btn k-btn-secondary k-btn-sm">
                    {comboUploading ? 'Yükleniyor…' : 'Bilgisayardan seç'}
                    <input type="file" accept="image/*" onChange={handleComboImageUpload} hidden disabled={comboUploading} />
                  </label>
                  <input className="k-input" placeholder="veya görsel adresi (https://…)" value={comboForm.imageUrl} onChange={(e) => setComboForm((p) => ({ ...p, imageUrl: e.target.value }))} />
                </div>
              </div>
            </div>
            <div className="km-span"><Switch checked={comboForm.isAvailable} onChange={(v) => setComboForm((p) => ({ ...p, isAvailable: v }))} label="Satışta" /></div>
          </div>
          <div className="km-options">
            <div className="km-options-head"><div><strong>Kombo içeriği *</strong></div></div>
            {comboForm.items.map((it, i) => (
              <div key={i} className="km-option">
                <div><b className="tnum">{it.quantity}×</b> {it.productName}</div>
                <button type="button" className="k-icon-btn km-danger" onClick={() => removeComboItem(i)}><Icon name="x" size={16} /></button>
              </div>
            ))}
            <div className="km-inline">
              <select className="k-select" value={comboItem.productId} onChange={(e) => setComboItem((p) => ({ ...p, productId: e.target.value }))}>
                <option value="">Ürün seçin</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({tl(p.price)})</option>)}
              </select>
              <input className="k-input" style={{ width: 90 }} type="number" min="1" placeholder="Adet" value={comboItem.quantity} onChange={(e) => setComboItem((p) => ({ ...p, quantity: e.target.value }))} />
              <button type="button" className="k-btn k-btn-secondary" onClick={addComboItem}><Icon name="plus" size={15} /></button>
            </div>
          </div>
        </Modal>
      )}

      {/* Kategori indirimi */}
      {showCategoryDiscountForm && (
        <Modal
          title="Kategori indirimi"
          width={440}
          onClose={() => setShowCategoryDiscountForm(false)}
          footer={(
            <>
              <button type="button" className="k-btn k-btn-secondary" onClick={() => setShowCategoryDiscountForm(false)}>Vazgeç</button>
              <button type="button" className="k-btn k-btn-primary" onClick={applyCategoryDiscount} disabled={categoryDiscountLoading}>{categoryDiscountLoading ? 'Uygulanıyor…' : 'Uygula'}</button>
            </>
          )}
        >
          <label className="k-field"><span>Kategori *</span>
            <select className="k-select" value={categoryDiscountForm.targetCategory} onChange={(e) => setCategoryDiscountForm((p) => ({ ...p, targetCategory: e.target.value }))}>
              <option value="">Kategori seçin</option>
              {categories.map((c) => <option key={c.id} value={c.name}>{c.name} ({products.filter((p) => p.category === c.name).length} ürün)</option>)}
            </select>
          </label>
          <div className="k-field"><span>İndirim türü</span>
            <div className="k-seg">
              {[['percent', 'Yüzde (%)'], ['fixed', 'Sabit (TL)']].map(([v, l]) => <button key={v} type="button" className={categoryDiscountForm.discountType === v ? 'on' : ''} onClick={() => setCategoryDiscountForm((p) => ({ ...p, discountType: v }))}>{l}</button>)}
            </div>
          </div>
          <label className="k-field"><span>İndirim değeri * {categoryDiscountForm.discountType === 'percent' ? '(1–99)' : '(TL)'}</span>
            <input className="k-input" type="number" min="1" max={categoryDiscountForm.discountType === 'percent' ? '99' : undefined} value={categoryDiscountForm.discountValue} onChange={(e) => setCategoryDiscountForm((p) => ({ ...p, discountValue: e.target.value }))} />
          </label>
        </Modal>
      )}

      {/* Toplu indirim */}
      {showBulkDiscountForm && (
        <Modal
          title="Toplu indirim"
          width={440}
          onClose={() => setShowBulkDiscountForm(false)}
          footer={(
            <>
              <button type="button" className="k-btn k-btn-secondary" onClick={() => setShowBulkDiscountForm(false)}>Vazgeç</button>
              <button type="button" className="k-btn k-btn-primary" onClick={bulkSetDiscount} disabled={bulkLoading}>{bulkLoading ? 'Uygulanıyor…' : 'Uygula'}</button>
            </>
          )}
        >
          <label className="k-field"><span>Hangi ürünlere?</span>
            <select className="k-select" value={bulkDiscountForm.targetCategory} onChange={(e) => setBulkDiscountForm((p) => ({ ...p, targetCategory: e.target.value }))}>
              <option value="all">Tüm ürünler</option>
              {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </label>
          <div className="k-field"><span>İndirim türü</span>
            <div className="k-seg">
              {[['percent', 'Yüzde (%)'], ['fixed', 'Sabit (TL)']].map(([v, l]) => <button key={v} type="button" className={bulkDiscountForm.discountType === v ? 'on' : ''} onClick={() => setBulkDiscountForm((p) => ({ ...p, discountType: v }))}>{l}</button>)}
            </div>
          </div>
          <label className="k-field"><span>İndirim değeri * {bulkDiscountForm.discountType === 'percent' ? '(1–99)' : '(TL)'}</span>
            <input className="k-input" type="number" min="1" max={bulkDiscountForm.discountType === 'percent' ? '99' : undefined} value={bulkDiscountForm.discountValue} onChange={(e) => setBulkDiscountForm((p) => ({ ...p, discountValue: e.target.value }))} />
          </label>
        </Modal>
      )}

      {/* Onay penceresi */}
      {confirmState && (
        <Modal
          title={confirmState.title}
          width={420}
          onClose={() => setConfirmState(null)}
          footer={(
            <>
              <button type="button" className="k-btn k-btn-secondary" onClick={() => setConfirmState(null)}>Vazgeç</button>
              <button type="button" className="k-btn k-btn-danger-solid" onClick={async () => { const fn = confirmState.onConfirm; setConfirmState(null); await fn(); }}>{confirmState.confirmLabel}</button>
            </>
          )}
        >
          {confirmState.text}
        </Modal>
      )}
    </div>
  );
}

export default Menu;
